import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { SCENE_CONSTANTS } from './constants.js'

export class SceneManager {
  constructor (container) {
    this.container = container
    this.scene = null
    this.renderer = null
    this.camera = null
    this.controls = null
    this.resizeHandler = null
  }

  init () {
    this.createScene()
    this.createRenderer()
    this.createCamera()
    this.createControls()
    this.setupLighting()
    this.setupEnvironment()
    this.setupResizeHandler()
  }

  createScene () {
    this.scene = new THREE.Scene()
  }

  createRenderer () {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight)
    this.renderer.setClearColor(SCENE_CONSTANTS.COLORS.SCENE_BACKGROUND)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.container.appendChild(this.renderer.domElement)
  }

  createCamera () {
    this.camera = new THREE.PerspectiveCamera(
      60,
      this.container.clientWidth / this.container.clientHeight,
      0.1,
      2000
    )
    this.camera.position.set(
      SCENE_CONSTANTS.POSITIONS.CAMERA_INITIAL_X,
      SCENE_CONSTANTS.POSITIONS.CAMERA_INITIAL_Y,
      SCENE_CONSTANTS.POSITIONS.CAMERA_INITIAL_Z
    )
  }

  createControls () {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.maxDistance = 400
    this.controls.target.set(
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_X,
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_Y,
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_Z
    )
    this.camera.lookAt(
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_X,
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_Y,
      SCENE_CONSTANTS.POSITIONS.CAMERA_TARGET_Z
    )
    this.controls.object.up.set(0, 1, 0)
    this.controls.update()
    this.controls.addEventListener('change', () => this.render())
  }

  setupLighting () {
    const directionalLight = new THREE.DirectionalLight(
      0xffffff,
      SCENE_CONSTANTS.LIGHTING.DIRECTIONAL_INTENSITY
    )
    directionalLight.position.set(
      SCENE_CONSTANTS.LIGHTING.DIRECTIONAL_X,
      SCENE_CONSTANTS.LIGHTING.DIRECTIONAL_Y,
      SCENE_CONSTANTS.LIGHTING.DIRECTIONAL_Z
    )
    this.configureShadow(directionalLight)
    this.scene.add(directionalLight)

    const fillLight = new THREE.DirectionalLight(
      0xffffff,
      SCENE_CONSTANTS.LIGHTING.FILL_INTENSITY
    )
    fillLight.position.set(
      SCENE_CONSTANTS.LIGHTING.FILL_X,
      SCENE_CONSTANTS.LIGHTING.FILL_Y,
      SCENE_CONSTANTS.LIGHTING.FILL_Z
    )
    this.configureShadow(fillLight)
    this.scene.add(fillLight)

    const ambientLight = new THREE.AmbientLight(
      0xffffff,
      SCENE_CONSTANTS.LIGHTING.AMBIENT_INTENSITY
    )
    this.scene.add(ambientLight)
  }

  configureShadow (light) {
    light.castShadow = true
    light.shadow.mapSize.width = SCENE_CONSTANTS.SHADOWS.MAP_SIZE
    light.shadow.mapSize.height = SCENE_CONSTANTS.SHADOWS.MAP_SIZE
    light.shadow.camera.near = SCENE_CONSTANTS.SHADOWS.CAMERA_NEAR
    light.shadow.camera.far = SCENE_CONSTANTS.SHADOWS.CAMERA_FAR
    light.shadow.camera.left = SCENE_CONSTANTS.SHADOWS.CAMERA_LEFT
    light.shadow.camera.right = SCENE_CONSTANTS.SHADOWS.CAMERA_RIGHT
    light.shadow.camera.top = SCENE_CONSTANTS.SHADOWS.CAMERA_TOP
    light.shadow.camera.bottom = SCENE_CONSTANTS.SHADOWS.CAMERA_BOTTOM
  }

  setupEnvironment () {
    const groundGeometry = new THREE.PlaneGeometry(
      SCENE_CONSTANTS.GEOMETRY.GROUND_SIZE,
      SCENE_CONSTANTS.GEOMETRY.GROUND_SIZE
    )
    const groundMaterial = new THREE.ShadowMaterial({
      color: 0x000000, // Shadow color (black)
      opacity: 0.3 // Shadow opacity
    })
    const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial)
    groundPlane.rotation.x = -Math.PI / 2
    groundPlane.position.y = SCENE_CONSTANTS.POSITIONS.GROUND_Y
    groundPlane.receiveShadow = true
    this.scene.add(groundPlane)
  }

  setupResizeHandler () {
    this.resizeHandler = () => this.handleResize()
    window.addEventListener('resize', this.resizeHandler)
  }

  handleResize () {
    const width = this.container.clientWidth
    const height = this.container.clientHeight
    this.renderer.setSize(width, height)
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.render()
  }

  render () {
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera)
    }
  }

  add (object) {
    this.scene.add(object)
  }

  fitCameraToKeyboard (keyboardGroup, padding = 1.5) {
    if (!keyboardGroup) {
      console.warn('No keyboard group provided for camera fitting')
      return
    }

    const box = new THREE.Box3().setFromObject(keyboardGroup)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())

    // Use a very close distance for keyboards
    const maxDimension = Math.max(size.x, size.z) // Use X and Z (width/depth), ignore Y (height)
    const distance = maxDimension * 0.55
    // Position camera more from the top
    const offset = new THREE.Vector3(0, 0.8, 0.3).normalize()
    const newPosition = new THREE.Vector3().addVectors(center, offset.multiplyScalar(distance))

    this.camera.position.copy(newPosition)
    this.controls.target.copy(center)
    this.camera.lookAt(center)
    this.controls.update()
  }

  dispose () {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }

    if (this.controls) {
      this.controls.dispose()
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.isMesh) {
          object.geometry.dispose()
          if (object.material.dispose) {
            object.material.dispose()
          }
        }
      })
      this.scene.clear()
    }

    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.forceContextLoss()
      this.renderer.domElement.remove()
    }
  }
}
